import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  Clock,
  ExternalLink,
  FileText,
  FlaskConical,
  ListChecks,
  Lock,
  PlayCircle,
  Target,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { Alert } from '@/components/ui/Alert';
import { Badge, PublicationBadge } from '@/components/ui/Badge';
import { ButtonLink } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { FullPageSpinner } from '@/components/ui/Spinner';
import { useAuth } from '@/features/auth/AuthContext';
import { formatMinutes } from '@/lib/format';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { fetchers, queryKeys } from '@/services/queries';

export function ModuleDetailPage() {
  const { identifier = '' } = useParams();
  const { user } = useAuth();
  const module = useQuery({
    queryKey: queryKeys.module(identifier),
    queryFn: () => fetchers.module(identifier),
    retry: false,
  });

  if (module.isLoading) return <FullPageSpinner label="Chargement du module…" />;
  if (module.isError || !module.data) return <NotFoundPage />;

  const data = module.data;
  const firstChapter = data.chapters[0];

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <Link
        to={data.track_slug ? `/parcours/${data.track_slug}` : '/parcours'}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted hover:text-fg"
      >
        <ArrowLeft className="h-4 w-4" />
        Retour au parcours
      </Link>

      <header className="mt-6">
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            tone={data.track_code === 'A' ? 'tech' : data.track_code === 'B' ? 'ai' : 'agent'}
          >
            {data.code}
          </Badge>
          <PublicationBadge status={data.status} />
          <Badge tone="neutral">{data.level}</Badge>
          <Badge tone="neutral">
            <Clock className="h-3 w-3" />
            {data.duration_hours} h
          </Badge>
        </div>
        <h1 className="mt-4 text-3xl font-bold tracking-tight">{data.title}</h1>
        <p className="mt-3 max-w-3xl text-muted">{data.summary}</p>
      </header>

      {!user ? (
        <Alert tone="info" title="Contenu réservé" className="mt-8">
          Le programme ci-dessous est public. La lecture des chapitres, les travaux pratiques et les
          quiz nécessitent une inscription au parcours.{' '}
          <Link to="/connexion" className="font-semibold text-tech hover:underline">
            Se connecter
          </Link>
        </Alert>
      ) : null}

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Chapters */}
          <Card>
            <CardHeader className="flex items-center justify-between gap-3">
              <CardTitle as="h2" className="flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-tech" />
                Chapitres ({data.chapters.length})
              </CardTitle>
              {user && firstChapter ? (
                <ButtonLink to={`/app/modules/${data.code}/chapitres/${firstChapter.slug}`} size="sm">
                  <PlayCircle className="h-4 w-4" />
                  Commencer
                </ButtonLink>
              ) : null}
            </CardHeader>
            <CardBody className="p-0">
              {data.chapters.length === 0 ? (
                <p className="px-5 py-6 text-sm text-muted">
                  Les chapitres de ce module sont en cours de production.
                </p>
              ) : (
                <ol className="divide-y divide-border">
                  {data.chapters.map((chapter, index) => (
                    <li key={chapter.id} className="flex items-center gap-4 px-5 py-3.5">
                      <span
                        className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-surface-2
                                   text-xs font-bold tabular-nums text-muted"
                        aria-hidden="true"
                      >
                        {index + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-fg">{chapter.title}</p>
                        {chapter.subtitle ? (
                          <p className="truncate text-xs text-faint">{chapter.subtitle}</p>
                        ) : null}
                      </div>
                      <span className="shrink-0 text-xs tabular-nums text-faint">
                        {formatMinutes(chapter.estimated_minutes)}
                      </span>
                      {user ? (
                        <Link
                          to={`/app/modules/${data.code}/chapitres/${chapter.slug}`}
                          className="shrink-0 text-xs font-semibold text-tech hover:underline"
                        >
                          Lire
                        </Link>
                      ) : (
                        <Lock className="h-3.5 w-3.5 shrink-0 text-faint" aria-label="Réservé" />
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </CardBody>
          </Card>

          {/* Labs */}
          {data.labs.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2" className="flex items-center gap-2">
                  <FlaskConical className="h-4 w-4 text-agent" />
                  Travaux pratiques ({data.labs.length})
                </CardTitle>
              </CardHeader>
              <CardBody className="p-0">
                <ul className="divide-y divide-border">
                  {data.labs.map((lab) => (
                    <li key={lab.id} className="flex items-center gap-4 px-5 py-3.5">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-fg">{lab.title}</p>
                        <p className="text-xs text-faint">
                          {lab.difficulty} · {formatMinutes(lab.duration_minutes)}
                        </p>
                      </div>
                      {user ? (
                        <Link
                          to={`/app/tp/${lab.id}`}
                          className="shrink-0 text-xs font-semibold text-tech hover:underline"
                        >
                          Ouvrir
                        </Link>
                      ) : (
                        <Lock className="h-3.5 w-3.5 shrink-0 text-faint" aria-label="Réservé" />
                      )}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}

          {/* Quizzes */}
          {data.quizzes.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2" className="flex items-center gap-2">
                  <ListChecks className="h-4 w-4 text-ai" />
                  Évaluations ({data.quizzes.length})
                </CardTitle>
              </CardHeader>
              <CardBody className="p-0">
                <ul className="divide-y divide-border">
                  {data.quizzes.map((quiz) => (
                    <li key={quiz.id} className="flex items-center gap-4 px-5 py-3.5">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-fg">{quiz.title}</p>
                        <p className="text-xs text-faint">
                          {quiz.question_count} questions · seuil {quiz.pass_score_percent} % ·{' '}
                          {quiz.max_attempts} tentative{quiz.max_attempts > 1 ? 's' : ''}
                        </p>
                      </div>
                      {user ? (
                        <Link
                          to={`/app/quiz/${quiz.id}`}
                          className="shrink-0 text-xs font-semibold text-tech hover:underline"
                        >
                          Passer
                        </Link>
                      ) : (
                        <Lock className="h-3.5 w-3.5 shrink-0 text-faint" aria-label="Réservé" />
                      )}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}
        </div>

        <aside className="space-y-6">
          {data.objectives.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2" className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-tech" />
                  Objectifs
                </CardTitle>
              </CardHeader>
              <CardBody>
                <ul className="space-y-2">
                  {data.objectives.map((objective) => (
                    <li key={objective} className="flex gap-2 text-sm text-muted">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-ok" aria-hidden="true" />
                      {objective}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}

          {data.prerequisites.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2">Prérequis</CardTitle>
              </CardHeader>
              <CardBody>
                <ul className="ml-4 list-disc space-y-1.5 text-sm text-muted">
                  {data.prerequisites.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}

          {data.technologies.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2">Technologies</CardTitle>
              </CardHeader>
              <CardBody>
                <ul className="flex flex-wrap gap-1.5">
                  {data.technologies.map((tech) => (
                    <li key={tech} className="rounded bg-surface-2 px-2 py-0.5 text-2xs text-muted">
                      {tech}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}

          {data.evaluation ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2">Modalités d'évaluation</CardTitle>
              </CardHeader>
              <CardBody>
                <p className="text-sm text-muted">{data.evaluation}</p>
              </CardBody>
            </Card>
          ) : null}

          {data.documentation_links.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2">Documentation officielle</CardTitle>
              </CardHeader>
              <CardBody>
                <ul className="space-y-2">
                  {data.documentation_links.map((link) => (
                    <li key={link.url}>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-start gap-1.5 text-sm text-tech hover:underline"
                      >
                        <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        {link.label ?? link.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}

          {data.resources.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2" className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted" />
                  Ressources
                </CardTitle>
              </CardHeader>
              <CardBody>
                <ul className="space-y-2 text-sm">
                  {data.resources.map((resource) => (
                    <li key={resource.id} className="text-muted">
                      <span className="font-medium text-fg">{resource.title}</span>
                      {resource.description ? (
                        <span className="block text-xs text-faint">{resource.description}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
